<template>
  <canvas ref="myChart" width="800" height="400"></canvas>
</template>

<script>
import { markRaw, toRaw } from 'vue'
import Chart from 'chart.js/auto'

// Chart.js gets RAW objects, never Vue's reactive proxies — `markRaw` on the instance and
// `toRaw` on everything handed to it. Under V2's defineProperty reactivity the difference was
// survivable; V3 wraps every nested object in a Proxy on access, and Chart.js is built on
// identity: it caches resolved option scopes against the objects they came from and compares
// them by reference. Through a proxy, two reads of the same nested object are two different
// Proxy instances, so those caches never hit and the lookups behind them come back undefined.
//
// Reactivity also closed a loop here. Chart.js writes its own bookkeeping back onto the config
// it was given; when that config was the reactive `chartdata`, each write re-triggered the deep
// watcher below, which called update(), which wrote again — "Maximum call stack size exceeded".
// Pointing it at the raw object breaks the cycle: writes to a raw target bypass the proxy's set
// trap entirely, so only a real assignment from the parent re-enters this watcher.
//
// Which is why the symptom showed up on the Sales analytics view switcher rather than at load:
// SalesPerformance re-renders this component from scratch on a date-range change (`v-if` on
// `loadingData`), so the chart was rebuilt with a fresh config. Switching Sales -> Quotes only
// reassigns `chartData.datasets`, and that is the single path that goes through the watcher.
export default {
  name: 'line-chart',
  props: ['type', 'chartdata', 'options'],
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.chart = markRaw(
      new Chart(this.$refs['myChart'], {
        type: 'line',
        data: toRaw(this.chartdata),
        options: toRaw(this.options)
      })
    )
  },
  beforeUnmount() {
    this.chart?.destroy()
    this.chart = null
  },
  watch: {
    chartdata: {
      handler(value) {
        if (!this.chart) return
        this.chart.data = toRaw(value)
        this.chart.update()
      },
      deep: true
    }
  }
}
</script>
